import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { alertaGeneral } from "../../utils/alertas";
import apiClient from "../../utils/axiosPetition";
import { useDataUser } from "../../context/dataUserContext";

const ID_TIPOS = [{ value: 'CC', label: 'Cédula de ciudadanía' }, { value: 'CE', label: 'Cédula de extranjería' }, { value: 'NIT', label: 'NIT' }, { value: 'PA', label: 'Pasaporte' }];

const PagRespuestaAfiliadoEpayco = () => {
    const navigate = useNavigate();
    const { setIsAuth } = useDataUser();
    const [searchParams] = useSearchParams();
    const ref_payco = searchParams.get('ref_payco');
    const [isProcessing, setIsProcessing] = useState(true);
    // Tras pago exitoso: mostrar formulario de datos del comprador para la factura (pueden ser distintos al usuario)
    const [paymentData, setPaymentData] = useState(null);
    const [buyerForm, setBuyerForm] = useState({ firstName: '', lastName: '', idType: 'CC', idNumber: '', email: '', cellNumber: '', address: '', city: '', department: '' });
    const [submittingFactura, setSubmittingFactura] = useState(false);

    useEffect(() => {
        const procesarRegistro = async () => {
            if (!ref_payco) {
                setIsProcessing(false);
                return;
            }
            
            // Intentar obtener datos de localStorage primero
            let data = {};
            try {
                const localStorageData = localStorage.getItem('registro-data');
                if (localStorageData) {
                    data = JSON.parse(localStorageData);
                    console.log('📦 Datos recuperados de localStorage:', data);
                    console.log('   - Email:', data.email);
                    console.log('   - FullName:', data.fullName);
                    console.log('   - CellNumber:', data.cellNumber);
                    console.log('   - IdNumber:', data.idNumber);
                    console.log('   - Service:', data.service);
                } else {
                    console.warn('⚠️ No hay datos en localStorage');
                    // Intentar obtener de sessionStorage como respaldo
                    const sessionStorageData = sessionStorage.getItem('registro-data-backup');
                    if (sessionStorageData) {
                        data = JSON.parse(sessionStorageData);
                        console.log('📦 Datos recuperados de sessionStorage (backup):', data);
                        console.log('   - Email:', data.email);
                        console.log('   - FullName:', data.fullName);
                        console.log('   - CellNumber:', data.cellNumber);
                        console.log('   - IdNumber:', data.idNumber);
                        console.log('   - Service:', data.service);
                        // Restaurar en localStorage también
                        localStorage.setItem('registro-data', sessionStorageData);
                    } else {
                        console.warn('⚠️ No hay datos en sessionStorage tampoco');
                    }
                }
            } catch (error) {
                console.error('❌ Error al parsear datos de localStorage:', error);
            }
            
            try {
                // Polling: consultar estado del pago en ePayco repetidamente hasta que esté aprobado
                const pollPaymentStatus = async (ref, attempts = 40, interval = 3000) => {
                    for (let i = 0; i < attempts; i++) {
                        try {
                            const estadoPagoRes = await apiClient.get(`/api/epayco/consultar-estado?ref_payco=${ref}`);
                            const r = estadoPagoRes.data;
                            console.log(`🔁 Polling ePayco (attempt ${i + 1}/${attempts})`, { ref, x_respuesta: r.x_respuesta });
                            if (r.x_respuesta === 'Aceptada' || r.x_cod_response === '1') {
                                return r;
                            }
                        } catch (pollErr) {
                            console.warn('⚠️ Error polling ePayco:', pollErr.response?.data || pollErr.message);
                        }
                        // Esperar antes del siguiente intento
                        await new Promise(resolve => setTimeout(resolve, interval));
                    }
                    return null;
                };

                const res = await pollPaymentStatus(ref_payco);
                if (!res) {
                    alertaGeneral({
                        titulo: 'Pago en proceso',
                        messageHtml: 'El pago todavía se está procesando. Espera unos minutos e intente iniciar sesión; si no recibe credenciales, contacte soporte con la referencia: ' + ref_payco,
                        isError: true
                    }).then(() => {
                        navigate('/registro-pagado');
                    });
                    setIsProcessing(false);
                    return;
                }

                const x_respuesta = res.x_respuesta;
                const x_ref_payco = res.x_ref_payco || ref_payco;
                const x_id_factura = res.x_id_factura;
                // El referenceCode puede ser x_id_factura o x_ref_payco (igual que en el callback)
                // Pero también podemos usar directamente ref_payco de la URL como fallback
                const referenceCode = x_id_factura || x_ref_payco || ref_payco;

                console.log('📊 Estado del pago (final):', {
                    respuesta: x_respuesta,
                    ref_payco: ref_payco,
                    x_ref_payco: res.x_ref_payco,
                    x_id_factura: x_id_factura,
                    referenceCode: referenceCode,
                    emailBilling: res.x_email_billing
                });

                if (x_respuesta === "Aceptada") {
                    console.log('✅ Pago aceptado, iniciando obtención de datos...');
                    
                    // SIEMPRE intentar obtener datos del backend primero (es la fuente de verdad)
                    // El callback de ePayco ya debería haber guardado todos los datos
                    let datosPago = null;
                    let intentosBackend = 0;
                    const maxIntentosBackend = 10; // Aumentar intentos para dar tiempo al callback
                    
                    while (!datosPago && intentosBackend < maxIntentosBackend) {
                        try {
                            // Esperar un poco antes de intentar (el callback puede estar procesándose)
                            if (intentosBackend > 0) {
                                const waitTime = Math.min(intentosBackend * 2000, 15000); // 2s, 4s, 6s... máx 15s
                                console.log(`⏳ Esperando ${waitTime/1000}s antes de intento ${intentosBackend + 1}...`);
                                await new Promise(resolve => setTimeout(resolve, waitTime));
                            } else {
                                // Primer intento sin espera
                                console.log('🔄 Intento 1 de obtener datos del pago del backend...');
                            }
                            
                            console.log(`🔄 Intento ${intentosBackend + 1}/${maxIntentosBackend} de obtener datos del backend...`);
                            console.log('   - Buscando con referenceCode:', referenceCode);
                            console.log('   - O ref_payco:', ref_payco);
                            
                            // Intentar obtener los datos del pago desde el backend
                            try {
                                const datosPagoResponse = await apiClient.get(`/api/afiliado/datos-pago?referenceCode=${referenceCode}&ref_payco=${ref_payco}`);
                                datosPago = datosPagoResponse.data;
                                console.log('✅ Datos del pago obtenidos del backend:', datosPago);
                                break; // Salir del loop si se obtuvieron los datos
                            } catch (error1) {
                                // Si falla, intentar solo con ref_payco
                                if (ref_payco && ref_payco !== referenceCode) {
                                    console.log('   - Intentando solo con ref_payco:', ref_payco);
                                    try {
                                        const datosPagoResponse2 = await apiClient.get(`/api/afiliado/datos-pago?ref_payco=${ref_payco}`);
                                        datosPago = datosPagoResponse2.data;
                                        console.log('✅ Datos del pago obtenidos del backend (por ref_payco):', datosPago);
                                        break; // Salir del loop si se obtuvieron los datos
                                    } catch (error2) {
                                        throw error2;
                                    }
                                } else {
                                    throw error1;
                                }
                            }
                        } catch (backendError) {
                            intentosBackend++;
                            console.warn(`⚠️ Intento ${intentosBackend} fallido:`, backendError.response?.data?.message || backendError.message);
                        }
                    }
                    
                    // Si finalmente no se obtuvieron datos del backend después de reintentos, mostrar error detallado
                    if (!datosPago) {
                        console.error('❌ No se pudieron obtener los datos del pago después de', maxIntentosBackend, 'intentos');
                        alertaGeneral({
                            titulo: 'Error obteniendo datos de registro',
                            messageHtml: 'El pago fue aprobado pero no se pudieron cargar los datos de registro. Por favor, espere unos minutos e intente nuevamente, o contacte al soporte con la referencia de pago: ' + referenceCode,
                            isError: true
                        }).then(() => {
                            navigate('/registro-pagado');
                        });
                        setIsProcessing(false);
                        return;
                    }
                    
                    // Si el pago ya está registrado, redirigir al login
                    if (datosPago.yaRegistrado) {
                        alertaGeneral({
                            titulo: 'Registro completado',
                            messageHtml: 'Este pago ya ha sido procesado. Por favor, inicia sesión con sus credenciales.',
                        }).then(() => {
                            localStorage.removeItem('registro-data');
                            sessionStorage.removeItem('registro-data-backup');
                            navigate('/login');
                        });
                        setIsProcessing(false);
                        return;
                    }
                    
                    // Combinar datos: backend (fuente de verdad) + localStorage + ePayco (password solo desde localStorage)
                    let registrationData = {
                        email: datosPago.correoAfiliado || data.email || res.x_email_billing || '',
                        fullName: datosPago.nombreAfiliado || data.fullName || '',
                        cellNumber: datosPago.cellNumber || data.cellNumber || '',
                        idNumber: datosPago.idNumber || data.idNumber || '',
                        service: datosPago.service || data.service || '',
                        password: data.password || undefined,
                        referenceCode: referenceCode,
                        ref_payco: ref_payco,
                        aceptoAutorizacion: data.aceptoAutorizacion === true || data.aceptoAutorizacion === 'true'
                    };
                    
                    console.log('📋 Datos de registro finales compilados:', {
                        email: registrationData.email,
                        fullName: registrationData.fullName,
                        cellNumber: registrationData.cellNumber,
                        idNumber: registrationData.idNumber,
                        service: registrationData.service
                    });
                    
                    // Validar que tenemos todos los datos necesarios
                    if (!registrationData.email || !registrationData.fullName || !registrationData.cellNumber || !registrationData.idNumber || !registrationData.service) {
                        console.error('❌ Datos de registro incompletos después de obtener del backend:', registrationData);
                        console.error('   - Datos del backend:', datosPago);
                        console.error('   - Datos de localStorage:', data);
                        console.error('   - Datos de ePayco:', { email: res.x_email_billing });
                        
                        alertaGeneral({
                            titulo: 'Error',
                            messageHtml: 'Falta información para completar el registro. Por favor, contacte al soporte con la referencia de pago: ' + referenceCode,
                            isError: true
                        }).then(() => {
                            navigate('/registro-pagado');
                        });
                        setIsProcessing(false);
                        return;
                    }
                    
                    console.log('✅ Datos de registro preparados:', {
                        email: registrationData.email,
                        fullName: registrationData.fullName,
                        cellNumber: registrationData.cellNumber,
                        idNumber: registrationData.idNumber,
                        service: registrationData.service,
                        referenceCode: registrationData.referenceCode
                    });
                    
                    console.log('📤 Datos listos; se pedirán datos del comprador para la factura antes de completar registro.');

                    // Mostrar formulario de datos del comprador para la factura (pueden ser distintos al usuario)
                    const fullName = registrationData.fullName || '';
                    const parts = fullName.trim().split(/\s+/);
                    setBuyerForm({
                        firstName: parts[0] || '',
                        lastName: parts.slice(1).join(' ') || '',
                        idType: 'CC',
                        idNumber: registrationData.idNumber || '',
                        email: registrationData.email || '',
                        cellNumber: registrationData.cellNumber || '',
                        address: '',
                        city: '',
                        department: ''
                    });
                    setPaymentData({ registrationData, referenceCode, ref_payco });
                    setIsProcessing(false);
                    return;
                } else {
                    alertaGeneral({
                        titulo: 'Error',
                        messageHtml: 'La transacción no fue aprobada',
                        isError: true
                    }).then(() => {
                        navigate('/registro-pagado', { state: { tipoRegistro: 'prestador de servicios', data: data } });
                    });
                    setIsProcessing(false);
                }
            } catch (error) {
                console.error('Error consultando estado del pago:', error);
                alertaGeneral({
                    titulo: 'Error',
                    messageHtml: 'No se pudo verificar el estado del pago. Por favor, contacte al soporte.',
                    isError: true
                }).then(() => {
                    navigate('/registro-pagado');
                });
                setIsProcessing(false);
            }
        };
        
        procesarRegistro();
    }, [ref_payco, navigate]);

    const handleSubmitBuyerForm = async (e) => {
        e.preventDefault();
        if (!paymentData) return;
        const { registrationData, referenceCode, ref_payco } = paymentData;
        const { firstName, lastName, idType, idNumber, email, cellNumber, address, city, department } = buyerForm;
        if (!firstName?.trim() || !lastName?.trim() || !idNumber?.trim() || !email?.trim() || !city?.trim()) {
            alertaGeneral({ titulo: 'Datos incompletos', messageHtml: 'Completa nombre, apellido, documento, correo y ciudad del comprador para la factura.', isError: true });
            return;
        }
        setSubmittingFactura(true);
        try {
            await apiClient.post('/api/epayco/datos-comprador-factura', {
                referenceCode,
                ref_payco,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                idType: idType || 'CC',
                idNumber: (idNumber || '').trim().replace(/\D/g, '').slice(0, 15) || idNumber?.trim(),
                email: (email || '').trim(),
                cellNumber: (cellNumber || '').trim(),
                address: (address || '').trim() || undefined,
                city: (city || '').trim() || undefined,
                department: (department || '').trim() || undefined
            });
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Error al generar la factura';
            alertaGeneral({ titulo: 'Error al generar factura', messageHtml: msg, isError: true }).then(() => setSubmittingFactura(false));
            return;
        }
        setSubmittingFactura(false);
        // Usar los datos actuales del formulario para el registro (no los iniciales): nombre, email, etc. corregidos por el usuario
        const fullNameFromForm = [firstName, lastName].filter(Boolean).join(' ').trim();
        const payloadRegistro = {
            ...registrationData,
            fullName: fullNameFromForm || registrationData.fullName,
            email: (email || '').trim() || registrationData.email,
            cellNumber: (cellNumber || '').trim() || registrationData.cellNumber,
            idNumber: (idNumber || '').trim().replace(/\D/g, '').slice(0, 15) || registrationData.idNumber
        };
        // Completar registro (reintentos como antes)
        const maxIntentos = 6;
        for (let intento = 1; intento <= maxIntentos; intento++) {
            try {
                const res = await apiClient.post('/api/afiliado/registro', payloadRegistro);
                const data = res.data || {};
                localStorage.removeItem('registro-data');
                sessionStorage.removeItem('registro-data-backup');
                // Login automático: si el backend devolvió token, guardar sesión y redirigir al doctor
                if (data.token && data.role === 'doctor') {
                    localStorage.setItem('token', data.token);
                    localStorage.setItem('role', 'doctor');
                    if (data.doctorId) localStorage.setItem('doctorId', data.doctorId);
                    if (data.doctorName) localStorage.setItem('doctorName', data.doctorName);
                    if (data.doctorEmail) localStorage.setItem('doctorEmail', data.doctorEmail);
                    if (data.patientId) localStorage.setItem('patientId', data.patientId);
                    setIsAuth(true);
                    const estado = (data.doctorEstado || '').toLowerCase();
                    if (estado === 'no_aprobado' || estado === 'en_revision') {
                        navigate('/doctor/perfil', { replace: true });
                    } else {
                        navigate('/doctor/dashboard', { replace: true });
                    }
                } else {
                    alertaGeneral({
                        titulo: 'Transacción Aprobada',
                        messageHtml: 'Se ha generado la factura con los datos del comprador. Se le han enviado las credenciales al correo ' + (payloadRegistro.email || registrationData.email) + ' y WhatsApp. También se ha registrado como paciente.',
                    }).then(() => navigate('/login'));
                }
                return;
            } catch (error) {
                if (error.response?.status === 400 && error.response?.data?.message?.includes('No se ha encontrado un pago') && intento < maxIntentos) {
                    await new Promise(r => setTimeout(r, intento * 3000));
                    continue;
                }
                if (error.response?.data?.yaRegistrado) {
                    alertaGeneral({
                        titulo: 'Registro completado',
                        messageHtml: 'Este pago ya ha sido procesado. Por favor, inicia sesión con sus credenciales.',
                    }).then(() => {
                        localStorage.removeItem('registro-data');
                        sessionStorage.removeItem('registro-data-backup');
                        navigate('/login');
                    });
                    return;
                }
                if (intento >= maxIntentos) {
                    const errorMessage = error.response?.data?.message || 'No se pudo completar el registro. Por favor, contacte al soporte.';
                    alertaGeneral({ titulo: 'Error en el registro', messageHtml: errorMessage, isError: true }).then(() => navigate('/registro-pagado'));
                    return;
                }
            }
        }
    };

    if (paymentData) {
        return (
            <div className="min-h-[80vh] flex items-center justify-center p-4 sm:p-6">
                <div className="w-full max-w-lg p-6 bg-white rounded-xl shadow-lg border border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 mb-1">Pago exitoso</h2>
                <p className="text-sm text-gray-600 mb-4">Indique los datos del comprador para generar la factura (pueden ser distintos a los del usuario que se registrará).</p>
                <form onSubmit={handleSubmitBuyerForm} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                            <input type="text" value={buyerForm.firstName} onChange={e => setBuyerForm(f => ({ ...f, firstName: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Nombre" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
                            <input type="text" value={buyerForm.lastName} onChange={e => setBuyerForm(f => ({ ...f, lastName: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Apellido" required />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de documento</label>
                        <select value={buyerForm.idType} onChange={e => setBuyerForm(f => ({ ...f, idType: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                            {ID_TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Número de documento *</label>
                        <input type="text" value={buyerForm.idNumber} onChange={e => setBuyerForm(f => ({ ...f, idNumber: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Documento" required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Correo del comprador *</label>
                        <input type="email" value={buyerForm.email} onChange={e => setBuyerForm(f => ({ ...f, email: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="correo@ejemplo.com" required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Celular</label>
                        <input type="text" value={buyerForm.cellNumber} onChange={e => setBuyerForm(f => ({ ...f, cellNumber: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Celular" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                        <input type="text" value={buyerForm.address} onChange={e => setBuyerForm(f => ({ ...f, address: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Calle, número, barrio (opcional)" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad *</label>
                            <input type="text" value={buyerForm.city} onChange={e => setBuyerForm(f => ({ ...f, city: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Ej. Bogotá, Medellín" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Departamento</label>
                            <input type="text" value={buyerForm.department} onChange={e => setBuyerForm(f => ({ ...f, department: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Ej. Cundinamarca, Antioquia" />
                        </div>
                    </div>
                    <button type="submit" disabled={submittingFactura} className="w-full mt-2 py-2.5 px-4 rounded-lg font-medium text-white bg-[#1f94c2] hover:bg-[#187a9e] disabled:opacity-60 disabled:cursor-not-allowed">
                        {submittingFactura ? 'Generando factura...' : 'Generar factura y completar registro'}
                    </button>
                </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[80vh] flex flex-col items-center justify-center p-4">
            {isProcessing ? (
                <div className="flex flex-col items-center justify-center gap-6 max-w-sm w-full px-4">
                    <div className="w-16 h-16 rounded-full border-4 border-[#1f94c2]/25 border-t-[#1f94c2] animate-spin" aria-hidden="true" />
                    <div className="text-center space-y-2">
                        <p className="text-lg font-semibold text-gray-800">Procesando registro y pago</p>
                        <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
                            <span className="inline-flex gap-1">
                                <span className="w-2 h-2 rounded-full bg-[#1f94c2] animate-bounce [animation-delay:0ms]" />
                                <span className="w-2 h-2 rounded-full bg-[#1f94c2] animate-bounce [animation-delay:150ms]" />
                                <span className="w-2 h-2 rounded-full bg-[#1f94c2] animate-bounce [animation-delay:300ms]" />
                            </span>
                        </p>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default PagRespuestaAfiliadoEpayco; 